import React, { FC, useState, useMemo } from 'react';
import { styled } from 'linaria/lib/react';

import { useGetUserPickedTopics } from 'graphQL/cards/userCards/hooks';
import { COLORS, FONTS } from 'styles/constants';
import useTheme from 'helpers/useTheme';
import Loader from 'components/UI/Loader';

interface IPicksFilterProps {
  onFiltersChange: (filters: { topicIds?: number[]; mediaTagNames?: string[] }) => void;
}

const StyledFilterContainer = styled.div<{ isDark: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 0;
  align-items: center;
  background: ${({ isDark }) => (isDark ? COLORS.black[100] : COLORS.white[100])};
`;

const StyledFilterSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 378px;
`;

const StyledScrollContainer = styled.div`
  display: flex;
  width: 378px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledFilterChips = styled.div`
  display: flex;
  gap: 8px;
  padding: 0 12px;
  min-width: max-content;
`;

const StyledFilterChip = styled.button<{ isSelected: boolean; isDark: boolean; isMediaType?: boolean }>`
  display: flex;
  padding: 10px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 22px;
  background: ${({ isSelected, isDark }) => {
    if (isSelected) {
      return isDark ? COLORS.white[100] : COLORS.black[100];
    }
    return 'transparent';
  }};
  color: ${({ isSelected, isDark }) => {
    if (isSelected) {
      return isDark ? COLORS.black[100] : COLORS.white[100];
    }
    return isDark ? COLORS.white[80] : COLORS.black[60];
  }};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: ${FONTS.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  white-space: nowrap;
`;

const PicksFilter: FC<IPicksFilterProps> = ({ onFiltersChange }) => {
  const isDark = useTheme();
  const { topics, mediaTags, loading } = useGetUserPickedTopics();

  const [selectedTopicIds, setSelectedTopicIds] = useState<number[]>([]);
  const [selectedMediaTagNames, setSelectedMediaTagNames] = useState<string[]>([]);

  // Helper function to capitalize media tag names
  const capitalizeMediaTagName = (name: string): string => {
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  // Memoize filter options to avoid unnecessary re-renders
  const allFilterOptions = useMemo(() => {
    const mediaTypeOptions = mediaTags.map((tag) => ({
      id: tag.id,
      name: tag.name,
      displayName: capitalizeMediaTagName(tag.name),
      type: 'media' as const,
    }));

    // Get all media tag names to exclude from topics (case-insensitive, trimmed)
    const mediaTagNames = new Set(mediaTags.map((tag) => tag.name.toLowerCase().trim()));

    // Filter out topics that are also media tags (case-insensitive, trimmed comparison)
    const topicOptions = topics
      .filter((topic) => !mediaTagNames.has(topic.name.toLowerCase().trim()))
      .map((topic) => ({
        id: topic.id,
        name: topic.name,
        type: 'topic' as const,
      }));

    return { mediaTypeOptions, topicOptions };
  }, [mediaTags, topics]);

  const handleTopicToggle = (topicId: number) => {
    const newSelectedTopicIds = selectedTopicIds.includes(topicId)
      ? selectedTopicIds.filter((id) => id !== topicId)
      : [...selectedTopicIds, topicId];

    setSelectedTopicIds(newSelectedTopicIds);
    onFiltersChange({
      topicIds: newSelectedTopicIds.length > 0 ? newSelectedTopicIds : undefined,
      mediaTagNames: selectedMediaTagNames.length > 0 ? selectedMediaTagNames : undefined,
    });
  };

  const handleMediaTagToggle = (mediaTagName: string) => {
    const newSelectedMediaTagNames = selectedMediaTagNames.includes(mediaTagName)
      ? selectedMediaTagNames.filter((name) => name !== mediaTagName)
      : [...selectedMediaTagNames, mediaTagName];

    setSelectedMediaTagNames(newSelectedMediaTagNames);
    onFiltersChange({
      topicIds: selectedTopicIds.length > 0 ? selectedTopicIds : undefined,
      mediaTagNames: newSelectedMediaTagNames.length > 0 ? newSelectedMediaTagNames : undefined,
    });
  };

  if (loading) {
    return (
      <StyledFilterContainer isDark={isDark}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Loader isDark={isDark} size={18} />
        </div>
      </StyledFilterContainer>
    );
  }

  return (
    <StyledFilterContainer isDark={isDark}>
      {/* Media Types Row - Top row with all media types */}
      {allFilterOptions.mediaTypeOptions.length > 0 && (
        <StyledFilterSection>
          <StyledScrollContainer>
            <StyledFilterChips>
              {allFilterOptions.mediaTypeOptions.map((mediaTag) => (
                <StyledFilterChip
                  key={`media-${mediaTag.id}`}
                  isSelected={selectedMediaTagNames.includes(mediaTag.name)}
                  isDark={isDark}
                  isMediaType
                  onClick={() => handleMediaTagToggle(mediaTag.name)}
                >
                  {mediaTag.displayName}
                </StyledFilterChip>
              ))}
            </StyledFilterChips>
          </StyledScrollContainer>
        </StyledFilterSection>
      )}

      {/* Topics Row - Bottom row with maximum 8 topics */}
      {allFilterOptions.topicOptions.length > 0 && (
        <StyledFilterSection>
          <StyledScrollContainer>
            <StyledFilterChips>
              {allFilterOptions.topicOptions.slice(0, 8).map((topic) => (
                <StyledFilterChip
                  key={`topic-${topic.id}`}
                  isSelected={selectedTopicIds.includes(topic.id)}
                  isDark={isDark}
                  onClick={() => handleTopicToggle(topic.id)}
                >
                  {topic.name}
                </StyledFilterChip>
              ))}
            </StyledFilterChips>
          </StyledScrollContainer>
        </StyledFilterSection>
      )}
    </StyledFilterContainer>
  );
};

export default PicksFilter;
